import { useSite } from '@on3/ui-lib/src/contexts/SiteProvider';
import clsx from 'clsx';
import Arrow from 'components/Svg/Arrow';
import { PrimaryButton } from 'components/UI/Buttons/PrimaryButton';
import { Text } from 'components/UI/Typography/Text';
import { useCallback, useMemo } from 'react';

import type { IVisitListProps } from './VisitList';
import styles from './VisitListColumnHeaders.module.scss';

export const VisitListColumnHeaders = ({
  hide: hideToggle,
  pagination,
  relatedModel,
  setHide,
}: IVisitListProps) => {
  const { status, date } = relatedModel || {};
  const { count } = pagination || {};

  const { currentSite } = useSite();
  const { primaryColor } = currentSite || {};
  const teamPrimaryColor = primaryColor || '#1d252c';
  const getVariant = hideToggle ? 'h5' : 'h6';
  const isCurrent = status === 'Current';
  const teamColor = hideToggle ? 'primary' : 'initial';
  const hide = hideToggle || isCurrent;
  const handleShow = useCallback(() => {
    if (setHide) setHide(!hideToggle);
  }, [setHide, hideToggle]);

  const listHeaderText = useMemo(() => {
    if (isCurrent) {
      return (
        <div className={styles.nextUpHeaderContainer}>
          <Text className={styles.nextUpText} color="primary" variant="h6">
            Next up:
          </Text>
          <span>
            {date}({count})
          </span>
        </div>
      );
    }

    return `${date} (${count})`;
  }, [count, date, isCurrent]);

  return (
    <header>
      <ul
        className={clsx(styles.columnHeaderContainer, {
          [styles.isCollapsed]: hideToggle,
          [styles.isCurrent]: isCurrent,
        })}
      >
        <li
          className={clsx(styles.listHeader, {
            [styles.collapsed]: hide,
          })}
        >
          <Text
            className={clsx({
              [styles.headerTextExpanded]: !hide,
            })}
            color={teamColor}
            component="h1"
            variant={getVariant}
          >
            {listHeaderText}
          </Text>
        </li>
        <li
          className={clsx(styles.vitalsHeader, {
            [styles.hide]: hide,
          })}
        >
          <Text
            aria-label="Position"
            color={teamColor}
            component="h4"
            variant="subtitle2"
          >
            Pos
          </Text>
        </li>
        <li
          className={clsx(styles.vitalsHeader, {
            [styles.hide]: hide,
          })}
        >
          <Text
            aria-label="Height"
            color={teamColor}
            component="h4"
            variant="subtitle2"
          >
            Ht
          </Text>
        </li>
        <li
          className={clsx(styles.vitalsHeader, {
            [styles.hide]: hide,
          })}
        >
          <Text
            aria-label="Weight"
            color={teamColor}
            component="h4"
            variant="subtitle2"
          >
            Wt
          </Text>
        </li>
        <li className={clsx(styles.columnHeader, { [styles.hide]: hide })}>
          <Text color={teamColor} component="h4" variant="subtitle2">
            Rating
          </Text>
        </li>
        <li className={clsx(styles.columnHeader, { [styles.hide]: hide })}>
          <Text color={teamColor} component="h4" variant="subtitle2">
            NIL Value
          </Text>
        </li>

        <li
          className={clsx(styles.hideButtonContainer, {
            [styles.noHeader]: hide,
          })}
        >
          <Text
            className={clsx(styles.columnHeader, { [styles.hide]: hide })}
            color={teamColor}
            component="h4"
            variant="subtitle2"
          >
            Status
          </Text>
          <PrimaryButton
            className={styles.btnHide}
            onClick={handleShow}
            variant="outlined"
          >
            <Arrow
              className={clsx({ [styles.rotate]: hideToggle })}
              fill={teamPrimaryColor}
            />
          </PrimaryButton>
        </li>
      </ul>
    </header>
  );
};
